import { formatISO } from 'date-fns';
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { RangeDate } from '@/globalTypes';
import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { useCan } from '@/utils/access';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  PlayerKYC6MatchesQuery,
  PlayerKYC6MatchesQueryVariables,
} from './__generated__/usePlayerKYC6Matches';
import { queryParams } from './queryParams';

const QUERY = gql`
  query PlayerKYC6Matches(
    $playerId: ID!
    $score: RangeInt
    $lastSearchedAt: RangeDate
    $kyc6MatchTypes: [KYC6MatchTypes!]
    $matchDecision: FilteringMatchDecision
    $orderBy: KYC6MatchesOrderBy
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      brands {
        name
        code
      }
      kyc6MatchesByPlayerId: kyc6MatchesByPlayerIdV2(
        playerId: $playerId
        score: $score
        lastSearchedAt: $lastSearchedAt
        kyc6MatchTypes: $kyc6MatchTypes
        matchDecision: $matchDecision
        orderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        edges {
          node {
            playerId
            playerUUID
            brand {
              name
            }
            score
            typeOfMatch
            pepTier
            lastSearchedAt
            resourceId
            version
            matchDecision
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

export default function usePlayerKyc6Matches(playerId: string) {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const isAgentAllowedToAccessKyc6 = useCan('MANAGE_AND_ACCESS_KYC6');

  const lastSearchedAt = useMemo((): RangeDate | undefined => {
    if (query.lastSearchedAtFrom && query.lastSearchedAtTo) {
      return {
        from: formatISO(query.lastSearchedAtFrom),
        to: formatISO(query.lastSearchedAtTo),
      };
    }
    return undefined;
  }, [query.lastSearchedAtFrom, query.lastSearchedAtTo]);

  const variables = {
    playerId,
    ...mapVariables(query),
    lastSearchedAt,
  };

  const [{ data, fetching }, refresh] = useQuery<
    PlayerKYC6MatchesQuery,
    PlayerKYC6MatchesQueryVariables
  >({
    query: QUERY,
    variables,
  });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    brands: data?.viewer.brands,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(
      query,
      setQuery,
      data?.viewer.kyc6MatchesByPlayerId.pageInfo,
    ),
    hasAccess: isAgentAllowedToAccessKyc6,
    entries: isAgentAllowedToAccessKyc6
      ? data?.viewer.kyc6MatchesByPlayerId?.edges?.map((edge) => edge?.node)
      : [],
  };
}
